import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Loader, fmt, StatusBadge, fmtDateTime, EmptyState, CopyButton } from "@/components/shared";
import { AdminPageHeader, ApproveButton, RejectButton } from "./adminShared";

const filters = ["all", "pending", "approved", "rejected"];

export default function AdminWithdrawals() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("pending");

  const load = useCallback(async () => {
    setItems(null);
    const r = await api.get("/admin/withdrawals", { params: { status } });
    setItems(r.data);
  }, [status]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => { try { await api.post(`/admin/withdrawals/${id}/approve`); toast.success("Withdrawal approved"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  const reject = async (id, reason) => { try { await api.post(`/admin/withdrawals/${id}/reject`, { reason }); toast.success("Withdrawal rejected"); load(); } catch (e) { toast.error(formatApiError(e)); } };

  return (
    <div data-testid="admin-withdrawals">
      <AdminPageHeader eyebrow="Management" title="Withdrawals"
        right={<div className="flex gap-2 flex-wrap">{filters.map((f) => (
          <button key={f} data-testid={`withdrawal-filter-${f}`} onClick={() => setStatus(f)}
            className={`px-4 py-2 rounded-lg text-sm border capitalize transition-colors ${status === f ? "bg-cyan-500/15 border-cyan-500/40 text-cyan-300" : "border-white/10 text-slate-400 hover:border-cyan-500/20"}`}>{f}</button>
        ))}</div>} />
      {items === null ? <Loader /> : items.length ? (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm min-w-[1080px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">
              {["ID", "User", "Gross", "Charity 10%", "Net Payout", "Network", "Wallet", "Date", "Status", "Admin", "Actions"].map((h) => <th key={h} className="pb-3 px-2 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((w) => (
                <tr key={w.id} className="border-b border-white/5 last:border-0" data-testid={`withdrawal-row-${w.id}`}>
                  <td className="py-3 px-2 font-mono text-xs text-slate-500">{w.id.slice(0, 8)}</td>
                  <td className="py-3 px-2"><p className="text-slate-200">{w.user_name}</p><p className="text-xs text-slate-500">{w.user_email}</p></td>
                  <td className="py-3 px-2 font-mono text-white">{fmt(w.gross_amount ?? w.amount)}</td>
                  <td className="py-3 px-2 font-mono text-rose-300">{w.charity_amount != null ? fmt(w.charity_amount) : "—"}</td>
                  <td className="py-3 px-2 font-mono text-emerald-300">{w.net_amount != null ? fmt(w.net_amount) : fmt(w.amount)}</td>
                  <td className="py-3 px-2 text-slate-300">{w.network}</td>
                  <td className="py-3 px-2"><div className="flex items-center gap-1"><span className="font-mono text-xs text-slate-500 max-w-[100px] truncate">{w.wallet_address}</span><CopyButton value={w.wallet_address} testId={`wd-copy-${w.id}`} label="" /></div></td>
                  <td className="py-3 px-2 text-slate-500">{fmtDateTime(w.created_at)}</td>
                  <td className="py-3 px-2"><StatusBadge status={w.status} />{w.rejection_reason && <p className="text-xs text-red-400 mt-1 max-w-[140px]">{w.rejection_reason}</p>}</td>
                  <td className="py-3 px-2 text-xs text-slate-500">{w.reviewed_by || "-"}</td>
                  <td className="py-3 px-2">
                    {w.status === "pending" ? (
                      <div className="flex gap-2">
                        <ApproveButton testId={`withdrawal-approve-${w.id}`} onConfirm={() => approve(w.id)} />
                        <RejectButton testId={`withdrawal-reject-${w.id}`} onConfirm={(r) => reject(w.id, r)} />
                      </div>
                    ) : <span className="text-xs text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <EmptyState title="No withdrawals" subtitle={`No ${status} withdrawals found.`} />}
    </div>
  );
}
